import { instanceBo } from "../utils/axios.create";

export interface AnalyticsProgramRef {
	id: string;
	name: string;
}

export type AnalyticsReportType =
	| "ENROLLMENT_SUMMARY"
	| "PROGRAM_ACTIVITY_SUMMARY"
	| "VISIT_DETAIL"
	| "CUSTOM";

export type AnalyticsReportFormat = "PDF" | "XLSX";

export type AnalyticsPreviewKind =
	| "SCALAR"
	| "KPI_GRID"
	| "PIE"
	| "BAR"
	| "TABLE";

export interface AnalyticsPreviewMetric {
	label: string;
	value: number | string;
}

export interface AnalyticsReportPreview {
	kind: AnalyticsPreviewKind;
	title: string;
	value?: number | string;
	valueLabel?: string;
	metrics?: AnalyticsPreviewMetric[];
	columns?: string[];
	rows?: Record<string, string>[];
	totalRows?: number;
}

export interface AnalyticsGeneratedReport {
	id: string;
	reportType: AnalyticsReportType;
	format: AnalyticsReportFormat;
	fileName: string;
	downloadUrl: string;
	expiresAt: string;
	preview?: AnalyticsReportPreview;
}

export interface AnalyticsTurnResult {
	chatSessionId: string;
	action: "ask" | "generate";
	message: string;
	report?: AnalyticsGeneratedReport;
}

export interface AnalyticsChatMessage {
	role: "USER" | "ASSISTANT";
	content: string;
	action?: "ask" | "generate";
	reportId?: string;
	ts: string;
}

export interface AnalyticsChatSummary {
	_id: string;
	title?: string;
	status: string;
	updatedAt?: string;
	createdAt?: string;
	reportRefs?: string[];
}

export interface AnalyticsChatSession extends AnalyticsChatSummary {
	messages: AnalyticsChatMessage[];
}

export interface AnalyticsReportItem {
	_id: string;
	reportType: AnalyticsReportType;
	format?: AnalyticsReportFormat;
	fileName: string;
	status: string;
	emailStatus: string;
	narrative?: string;
	createdAt?: string;
}

interface SendMessageArgs {
	message: string;
	chatSessionId?: string;
	userId: string;
	programs?: AnalyticsProgramRef[];
}

const sendMessage = async (
	args: SendMessageArgs,
): Promise<AnalyticsTurnResult> =>
	instanceBo
		.post("/analytics/chat", {
			message: args.message,
			chatSessionId: args.chatSessionId,
			userId: args.userId,
			programs: args.programs,
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const listChats = async (userId?: string): Promise<AnalyticsChatSummary[]> =>
	instanceBo
		.get("/analytics/chats", { params: userId ? { userId } : {} })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const getChat = async (chatSessionId: string): Promise<AnalyticsChatSession> =>
	instanceBo
		.get(`/analytics/chats/${chatSessionId}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const deleteChat = async (
	chatSessionId: string,
): Promise<{ deleted: boolean }> =>
	instanceBo
		.delete(`/analytics/chats/${chatSessionId}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const listReports = async (userId?: string): Promise<AnalyticsReportItem[]> =>
	instanceBo
		.get("/analytics/reports", { params: userId ? { userId } : {} })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const getReportDownload = async (
	reportId: string,
): Promise<{ downloadUrl: string; expiresAt: string; fileName: string }> =>
	instanceBo
		.get(`/analytics/reports/${reportId}/download`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

const emailReport = async (
	reportId: string,
	recipients: string[],
	userId: string,
): Promise<{ sent: number; recipients: string[] }> =>
	instanceBo
		.post(`/analytics/reports/${reportId}/email`, { recipients, userId })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

export const analyticsService = {
	sendMessage,
	listChats,
	getChat,
	deleteChat,
	listReports,
	getReportDownload,
	emailReport,
};
